/**
 * <arc-carousel> · Glow Best Sellers fan/arc carousel.
 * בסיס: assets/arc-carousel.js של התמה (הקוד שהיה בפרודקשן עד 25.10). מה שונה מסומן "שינוי:".
 * 2.11.2026: אותו קוד כמו ההדמיה המאושרת (raw/Website-Pages-Plan/homepage-arc-return/_source/src/js/arc-carousel.js).
 * בתמה הקשת מתחמשת בעצמה (#autoArm, אחרי load ובקרבת מקום), והתמונות הדחויות הן data-gh-src (snippets/catalog-card.liquid, img_defer).
 * NOTE: the RTL sign flag is `dirSign`, NOT `dir`: `dir` is a reflected DOM property on HTMLElement.
 */
class ArcCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-arc-slide]')];
    if (this.slides.length < 2) return;
    // שינוי: הקשת חוזרת גם למובייל, ולכן שער ה-matchMedia של 4.10 ירד.
    if (typeof window.initLazyScript === 'function') window.initLazyScript(this, this.init.bind(this));
    else this.init();
  }

  disconnectedCallback() {
    this.#removeListeners();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.tween) cancelAnimationFrame(this.tween);
  }

  init() {
    if (this.hasAttribute('loaded')) return;
    this.rtl = document.documentElement.dir === 'rtl' || document.dir === 'rtl';
    this.dirSign = this.rtl ? -1 : 1;
    this.reduceMql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduceMotion = this.reduceMql.matches;
    this.viewport = this.querySelector('[data-arc-viewport]');
    this.track = this.querySelector('[data-arc-track]');
    this.prevBtn = this.querySelector('[data-arc-prev]');
    this.nextBtn = this.querySelector('[data-arc-next]');
    this.details = this.slides.map((s) => s.querySelector('.catalog-card__detail'));
    this.badges = this.slides.map((s) => s.querySelector('.glow-badge'));
    this.medias = this.slides.map((s) => s.querySelector('.catalog-card__media'));
    this.cards = this.slides.map((s) => s.querySelector('.catalog-card'));
    this.count = this.slides.length;
    this.position = 0;
    this.velocity = 0;
    this.pointerActive = false;
    if (this.reduceMotion) {
      this.classList.add('arc-carousel--reduced');
      this.#initReducedMotion();
    } else {
      this.#measure();
      this.#addListeners();
      this.#render();
    }
    this.setAttribute('loaded', '');
    if (this.armed) this.arm();
    else this.#autoArm();
  }

  // בתמה: אחרי שהדף נטען (שום דבר לא מתחרה בתמונת ההירו), וכשהקשת במרחק 700px מהמסך
  #autoArm() {
    const go = () => {
      if (!('IntersectionObserver' in window)) { this.arm(); return; }
      const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); this.arm(); } }, { rootMargin: '700px 0px' });
      io.observe(this);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  /**
   * שינוי: 21 מוצרים. כשהסקשן מתקרב נטענות רק הצנצנות שליד המרכז, והשאר כשהקשת מסתובבת אליהן, 2.5 צעדים לפני שהן נראות.
   * #autoArm קורא לזה. בלי תנועה הרצועה שטוחה וגוללים בה, ולכן IO על הרצועה עצמה.
   */
  arm() {
    this.armed = true;
    if (!this.hasAttribute('loaded')) return;
    if (!this.reduceMotion) { this.#loadNear(); return; }
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); this.#load(e.target); } }), { root: this.viewport, rootMargin: '0px 400px' });
    this.slides.forEach((s) => io.observe(s));
  }

  #loadNear() {
    const c = Math.round(this.position / this.span);
    if (!this.armed || c === this.nearAt) return;
    this.nearAt = c;
    const reach = (this.mobile ? 1 : this.vis) + 2.5;
    this.slides.forEach((s, i) => { if (!s.dataset.near && Math.abs(this.#wrap(i * this.span - this.position)) / this.span < reach) this.#load(s); });
  }

  #load(slide) {
    slide.dataset.near = '1';
    for (const img of slide.querySelectorAll('.catalog-card__img--primary img[data-gh-src]')) {
      if (img.dataset.ghSrcset) { img.srcset = img.dataset.ghSrcset; img.removeAttribute('data-gh-srcset'); }
      img.src = img.dataset.ghSrc; img.removeAttribute('data-gh-src');
    }
    this.dispatchEvent(new CustomEvent('arc:near', { detail: slide }));
  }

  /**
   * שינוי: הגיאומטריה לא מחושבת כאן אלא נקראת מה-CSS (data-k בכל slide), ולכן הציור לפני ה-JS והציור שאחריו זהים.
   * T[k] = המצב של כרטיס במרחק k מהמרכז. בין מספרים שלמים #lerp מחליק (Hermite), בלי שבירות בתנועה.
   */
  #measure() {
    const T = { x: [0], y: [0], r: [0], s: [1], o: [1], f: [1], n: [1] };
    for (let k = 1; k <= 4; k += 1) {
      const el = this.slides.find((s) => s.dataset.k === String(k));
      const prev = el.style.cssText;
      el.style.cssText = '';
      const cs = getComputedStyle(el);
      const m = new DOMMatrixReadOnly(cs.transform);
      T.x[k] = Math.abs(m.e);
      T.y[k] = m.f;
      T.s[k] = Math.hypot(m.a, m.b);
      T.r[k] = Math.abs((Math.atan2(m.b, m.a) * 180) / Math.PI);
      T.o[k] = parseFloat(getComputedStyle(el.querySelector('.catalog-card__media')).opacity);
      T.f[k] = parseFloat(cs.getPropertyValue('--tf')) || 0;
      T.n[k] = parseFloat(cs.getPropertyValue('--n')) || 1;
      el.style.cssText = prev;
    }
    this.T = T;
    const root = getComputedStyle(this.closest('.ba') || this);
    this.vis = parseInt(root.getPropertyValue('--ba-vis'), 10) || 2;
    this.badgeMin = parseFloat(root.getPropertyValue('--ba-tag-min')) || 0;
    this.btn = parseFloat(root.getPropertyValue('--ba-btn')) || 44;
    // מובייל: התנועה של 4.10, והכרטיס סטטי. שום דבר בתוך הכרטיס לא מחושב לפי המיקום (ה-#render של המובייל נוגע רק ב-slide ובשקיפות התמונה)
    this.mobile = window.matchMedia('(max-width: 767.98px)').matches;
    this.arcR = T.y[1] / (1 - Math.cos((T.r[1] * Math.PI) / 180));
    if (this.mobile) this.slides.forEach((s, i) => {
      s.style.opacity = '';
      this.cards[i].style.removeProperty('--cs');
      if (this.badges[i]) this.badges[i].style.removeProperty('--bs');
      if (this.details[i]) { this.details[i].style.transform = ''; this.details[i].style.opacity = ''; }
    });
    this.span = T.x[1];
    this.trackLength = this.span * this.count;
  }

  /* cubic Hermite על טבלה (even: סימטרי סביב 0) */
  #lerp(tbl, t, even) {
    const i = Math.min(Math.floor(t), tbl.length - 2);
    const u = t - i;
    const at = (n) => (n < 0 ? (even ? tbl[-n] : -tbl[-n]) : tbl[Math.min(n, tbl.length - 1)]);
    const p0 = at(i), p1 = at(i + 1);
    const m0 = (at(i + 1) - at(i - 1)) / 2, m1 = (at(i + 2) - at(i)) / 2;
    const u2 = u * u, u3 = u2 * u;
    return (2 * u3 - 3 * u2 + 1) * p0 + (u3 - 2 * u2 + u) * m0 + (-2 * u3 + 3 * u2) * p1 + (u3 - u2) * m1;
  }

  #addListeners() {
    this.onPointerDown = this.#handlePointerDown.bind(this);
    this.onPointerMove = this.#handlePointerMove.bind(this);
    this.onPointerUp = this.#handlePointerUp.bind(this);
    this.onWheel = this.#handleWheel.bind(this);
    this.onNavClick = this.#handleNavClick.bind(this);
    this.onKeydown = this.#handleKeydown.bind(this);
    this.onResize = this.#handleResize.bind(this);
    this.onFocusIn = this.#handleFocusIn.bind(this);
    this.onDragStart = (e) => e.preventDefault();

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    // שינוי: גם pointercancel. במגע הדפדפן לוקח את הגלילה האנכית ושולח cancel, ובלי זה הקשת נשארה "תפוסה".
    window.addEventListener('pointercancel', this.onPointerUp);
    this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
    this.viewport.addEventListener('dragstart', this.onDragStart);
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onNavClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNavClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusin', this.onFocusIn);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('on:debounced-resize', this.onResize);
  }

  #removeListeners() {
    if (!this.viewport || !this.onPointerDown) return;
    this.viewport.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.viewport.removeEventListener('wheel', this.onWheel);
    this.viewport.removeEventListener('dragstart', this.onDragStart);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onNavClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNavClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusin', this.onFocusIn);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('on:debounced-resize', this.onResize);
  }

  #wrap(value) {
    const len = this.trackLength;
    let v = value % len;
    if (v > len / 2) v -= len;
    if (v < -len / 2) v += len;
    return v;
  }

  /** מציב כל כרטיס לפי המרחק שלו מהמרכז. מה שמעבר לטווח הנראה מוסתר, ודועך לפני כן כדי שלא "יקפוץ" פנימה. */
  #render() {
    const { T } = this;
    const end = this.vis + 0.85;
    for (let i = 0; i < this.count; i += 1) {
      const slide = this.slides[i];
      const offset = this.#wrap(i * this.span - this.position);
      const steps = offset / this.span;
      const at = Math.abs(steps);
      // מובייל: הנוסחאות של 4.10 (assets/arc-carousel.js, #render), בלי טבלה
      if (this.mobile && at < end) {
        if (slide.style.visibility !== 'visible') slide.style.visibility = 'visible';
        const tilt = this.dirSign * T.r[1] * steps;
        const dip = this.arcR * (1 - Math.cos((Math.abs(tilt) * Math.PI) / 180));
        slide.style.transform = `translate3d(${(this.dirSign * offset).toFixed(2)}px, ${dip.toFixed(2)}px, 0) rotate(${tilt.toFixed(3)}deg)`;
        slide.style.zIndex = 100 - Math.round(at * 10);
        if (this.medias[i]) this.medias[i].style.opacity = (1 - Math.min(at, 6) * 0.06).toFixed(3);
        slide.classList.toggle('is-centre', at < 0.5);
        continue;
      }
      if (at >= end) {
        if (slide.style.visibility !== 'hidden') slide.style.visibility = 'hidden';
        continue;
      }
      if (slide.style.visibility !== 'visible') slide.style.visibility = 'visible';
      const side = steps < 0 ? -1 : 1;
      const x = this.dirSign * side * this.#lerp(T.x, at, false);
      const tilt = this.dirSign * side * this.#lerp(T.r, at, false);
      const dip = this.#lerp(T.y, at, true);
      const sc = this.#lerp(T.s, at, true);
      const op = Math.min(1, Math.max(0, this.#lerp(T.o, Math.min(at, this.vis), true)));
      // שינוי: העמעום של הקצוות על התמונה בלבד (תגית וטקסט נשארים בניגודיות מלאה). הדעיכה ליציאה מהטווח על כל הכרטיס.
      const out = at > this.vis + 0.15 ? Math.max(0, 1 - (at - this.vis - 0.15) / 0.7) : 1;
      slide.style.transform = `translate3d(${x.toFixed(2)}px, ${dip.toFixed(2)}px, 0) rotate(${tilt.toFixed(3)}deg) scale(${sc.toFixed(4)})`;
      slide.style.opacity = out.toFixed(3);
      if (this.medias[i]) this.medias[i].style.opacity = op.toFixed(3);
      slide.style.zIndex = 100 - Math.round(at * 10);
      const tf = Math.min(1, Math.max(0, this.#lerp(T.f, at, true)));
      if (this.details[i]) this.details[i].style.opacity = tf.toFixed(3);
      // שינוי: הכרטיס מובלט בקנה מידה, והלב, ה-+, התגית והטקסט לא יורדים מתחת לגודל קריא ושמיש.
      this.cards[i].style.setProperty('--cs', Math.max(1, this.btn / (44 * sc)).toFixed(3));
      if (this.badges[i] && this.badgeMin) this.badges[i].style.setProperty('--bs', Math.max(1, this.badgeMin / (12 * sc)).toFixed(3));
      if (this.details[i]) this.details[i].style.transform = `scale(${(this.#lerp(T.n, at, true) / sc).toFixed(4)})`;
      slide.classList.toggle('is-centre', at < 0.5);
    }
    this.#loadNear();
  }

  // שינוי: הפיזיקה לפי זמן ולא לפי פריים, כדי שתהיה זהה ב-60Hz וב-120Hz (אייפון). ב-60Hz המספרים כמו בפרודקשן.
  #startAnimation() {
    if (this.raf) return;
    let last = performance.now();
    const step = (now) => {
      const f = Math.min(3, (now - last) / 16.67);
      last = now;
      if (!this.pointerActive) {
        this.position += this.velocity * f;
        this.velocity *= Math.pow(0.9, f);
        if (Math.abs(this.velocity) < 0.2) {
          this.velocity = 0;
          this.#snap(f);
        }
      }
      this.#render();
      if (this.velocity !== 0 || this.snapping) this.raf = requestAnimationFrame(step);
      else this.raf = null;
    };
    this.raf = requestAnimationFrame(step);
  }

  #snap(f = 1) {
    const nearest = Math.round(this.position / this.span) * this.span;
    const delta = nearest - this.position;
    if (Math.abs(delta) < 0.5) {
      this.position = nearest;
      this.snapping = false;
      return;
    }
    this.snapping = true;
    this.position += delta * (1 - Math.pow(0.8, f));
  }

  #handlePointerDown(evt) {
    if (evt.button !== undefined && evt.button !== 0) return;
    // שינוי: לחיצה בזמן מעבר של החיצים עוצרת אותו, כדי ששתי התנועות לא יתחרו על position.
    if (this.tween) { cancelAnimationFrame(this.tween); this.tween = null; }
    this.pointerActive = true;
    this.snapping = false;
    this.startX = evt.clientX;
    this.startPos = this.position;
    this.lastX = evt.clientX;
    this.velocity = 0;
    this.lastMoveT = 0;
    this.dragged = false;
    this.activePointerId = evt.pointerId != null ? evt.pointerId : null;
    this.viewport.classList.add('is-grabbing');
  }

  #handlePointerMove(evt) {
    if (!this.pointerActive) return;
    if (!this.dragged && Math.abs(evt.clientX - this.startX) <= 8) {
      this.lastX = evt.clientX;
      return;
    }
    if (!this.dragged) {
      this.dragged = true;
      this.viewport.classList.add('is-dragging');
      if (this.viewport.setPointerCapture && this.activePointerId != null) {
        try { this.viewport.setPointerCapture(this.activePointerId); } catch (e) { /* no-op */ }
      }
    }
    const dx = evt.clientX - this.lastX;
    this.lastX = evt.clientX;
    this.position -= this.dirSign * dx;
    const now = performance.now();
    const dt = Math.max(1, now - (this.lastMoveT || now - 16.67));
    this.lastMoveT = now;
    // שינוי: המהירות בפיקסלים לפריים של 16.67ms, מוחלקת. בפרודקשן זה היה ה-dx של האירוע האחרון, תלוי בקצב האירועים.
    const v = (-this.dirSign * dx * 16.67) / dt;
    this.velocity = this.velocity * 0.6 + v * 0.4;
    this.#render();
  }

  #handlePointerUp(evt) {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.viewport.classList.remove('is-grabbing', 'is-dragging');
    if (this.activePointerId != null && this.viewport.releasePointerCapture) {
      try { this.viewport.releasePointerCapture(this.activePointerId); } catch (e) { /* no-op */ }
    }
    this.activePointerId = null;
    // שינוי: אצבע או עכבר שנעצרו לפני השחרור לא "זורקים" את הקשת. בפרודקשן המהירות האחרונה נשמרה, והיא קפצה מוצר.
    if ((evt && evt.type === 'pointercancel') || performance.now() - (this.lastMoveT || 0) > 90) this.velocity = 0;
    if (this.dragged) this.#suppressNextClick();
    this.#startAnimation();
  }

  // שינוי: המלכודת פגה אחרי רגע. במגע לא מגיע click אחרי גרירה, והיא הייתה בולעת את הלחיצה הבאה על כרטיס.
  #suppressNextClick() {
    const handler = (e) => { e.preventDefault(); e.stopPropagation(); };
    this.viewport.addEventListener('click', handler, { capture: true, once: true });
    setTimeout(() => this.viewport.removeEventListener('click', handler, { capture: true }), 350);
  }

  // שינוי: רק גלגול אופקי (משטח מגע) מזיז את הקשת. בפרודקשן גם גלילה אנכית נתפסה, והדף נתקע מעל הקשת בלופ אינסופי.
  #handleWheel(evt) {
    if (Math.abs(evt.deltaX) <= Math.abs(evt.deltaY)) return;
    const delta = evt.deltaX;
    if (Math.abs(delta) < 1) return;
    evt.preventDefault();
    this.position += this.dirSign * delta;
    this.velocity = 0;
    this.snapping = false;
    clearTimeout(this.wheelSnapTimer);
    this.wheelSnapTimer = setTimeout(() => this.#startAnimation(), 90);
    this.#render();
  }

  #handleNavClick(evt) {
    this.#goBy(evt.currentTarget.name === 'next' ? 1 : -1);
  }

  #handleKeydown(evt) {
    if (evt.key === 'ArrowRight') {
      evt.preventDefault();
      this.#goBy(this.rtl ? -1 : 1);
    } else if (evt.key === 'ArrowLeft') {
      evt.preventDefault();
      this.#goBy(this.rtl ? 1 : -1);
    }
  }

  // שינוי: פוקוס מהמקלדת על כרטיס מביא אותו למרכז, כך שכל המוצרים נגישים ב-Tab והפוקוס תמיד נראה.
  #handleFocusIn(evt) {
    if (!evt.target.matches(':focus-visible')) return;
    const slide = evt.target.closest('[data-arc-slide]');
    if (!slide) return;
    const i = this.slides.indexOf(slide);
    if (i < 0) return;
    const offset = this.#wrap(i * this.span - this.position);
    if (Math.abs(offset) < this.span / 2) return;
    this.#animateTo(this.position + offset);
  }

  #goBy(steps) {
    this.snapping = false;
    this.velocity = 0;
    const base = this.tween ? this.target : Math.round(this.position / this.span) * this.span;
    this.#animateTo(base + steps * this.span);
  }

  #animateTo(target) {
    if (this.tween) cancelAnimationFrame(this.tween);
    this.target = target;
    const start = this.position;
    const change = target - start;
    const duration = 500;
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const frame = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      this.position = start + change * ease(t);
      this.#render();
      this.tween = t < 1 ? requestAnimationFrame(frame) : null;
    };
    this.tween = requestAnimationFrame(frame);
  }

  #handleResize() {
    if (this.reduceMotion) return;
    const index = Math.round(this.position / this.span);
    this.#measure();
    this.position = index * this.span;
    this.nearAt = null;
    this.#render();
  }

  /** Flat, accessible fallback: native horizontal scroll, no rotation. The strip opens on the centre product. */
  #initReducedMotion() {
    this.slides.forEach((s) => s.style.setProperty('--ba-order', s.dataset.k));
    const centre = this.slides.find((s) => s.dataset.k === '0');
    // WebKit מאפס scrollLeft שנקבע לפני סיום הפריסה, ולכן מרכזים שוב עד שהדף נטען, כל עוד המשתמשת עוד לא נגעה ברצועה.
    let touched = false;
    const centreStrip = () => {
      if (touched) return;
      const r = centre.getBoundingClientRect(), v = this.viewport.getBoundingClientRect();
      this.viewport.scrollLeft += r.left + r.width / 2 - (v.left + v.width / 2);
    };
    centreStrip();
    requestAnimationFrame(centreStrip);
    window.addEventListener('load', centreStrip, { once: true });
    setTimeout(centreStrip, 400);
    this.viewport.addEventListener('pointerdown', () => { touched = true; }, { once: true });
    const stepBy = (forward) => {
      touched = true;
      const w = centre.getBoundingClientRect().width + 16;
      this.viewport.scrollBy({ left: this.dirSign * (forward ? 1 : -1) * w, behavior: 'auto' });
    };
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => stepBy(false));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => stepBy(true));
  }
}

if (!window.customElements.get('arc-carousel')) window.customElements.define('arc-carousel', ArcCarousel);
